import { unByKey } from 'ol/Observable.js';

const DEFAULT_OPTIONS = {
  edgeThreshold: 56,
  panSpeedPx: 4
};

let activeSession = null;

function getPointerPixel(map, evt) {
  if (Array.isArray(evt?.pixel)) return evt.pixel;
  const viewport = map.getViewport?.();
  const rect = viewport?.getBoundingClientRect?.();

  if (rect && Number.isFinite(evt?.clientX) && Number.isFinite(evt?.clientY)) {
    return [evt.clientX - rect.left, evt.clientY - rect.top];
  }

  if (evt?.originalEvent) {
    return map.getEventPixel(evt.originalEvent);
  }
  return null;
}

function computeAxisStep(pixelValue, sizeValue, threshold, panSpeedPx) {
  if (!Number.isFinite(pixelValue) || !Number.isFinite(sizeValue)) return 0;

  if (pixelValue < threshold) {
    const ratio = (threshold - pixelValue) / threshold;
    return -Math.max(1, Math.round(panSpeedPx * ratio));
  }

  const edgeStart = sizeValue - threshold;
  if (pixelValue > edgeStart) {
    const ratio = (pixelValue - edgeStart) / threshold;
    return Math.max(1, Math.round(panSpeedPx * ratio));
  }

  return 0;
}

function applyAutoPan() {
  if (!activeSession) return;

  const { map, options } = activeSession;
  const view = map.getView?.();
  const size = map.getSize?.();

  if (!view || !Array.isArray(size) || size.length < 2) {
    activeSession.rafId = requestAnimationFrame(applyAutoPan);
    return;
  }

  const pixel = activeSession.lastPixel;
  const threshold = options.edgeThreshold;
  const panSpeedPx = options.panSpeedPx;

  const dx = computeAxisStep(pixel?.[0], size[0], threshold, panSpeedPx);
  const dy = computeAxisStep(pixel?.[1], size[1], threshold, panSpeedPx);

  if (dx || dy) {
    const centerPixel = [size[0] / 2, size[1] / 2];
    const targetPixel = [centerPixel[0] + dx, centerPixel[1] + dy];
    const targetCenter = map.getCoordinateFromPixel(targetPixel);

    if (targetCenter) {
      view.setCenter(targetCenter);
    }
  }

  activeSession.rafId = requestAnimationFrame(applyAutoPan);
}

export function startDrawAutoPan(map, options = {}) {
  if (!map) return;

  stopDrawAutoPan();

  const session = {
    map,
    options: {
      ...DEFAULT_OPTIONS,
      ...options,
      panSpeedPx: options.panSpeedPx ?? options.maxStepPx ?? DEFAULT_OPTIONS.panSpeedPx
    },
    lastPixel: null,
    moveKey: null,
    dragKey: null,
    leaveHandler: null,
    rafId: null
  };

  const updatePointer = evt => {
    session.lastPixel = getPointerPixel(map, evt);
  };

  session.moveKey = map.on('pointermove', updatePointer);
  session.dragKey = map.on('pointerdrag', updatePointer);
  session.leaveHandler = updatePointer;
  document.addEventListener('pointermove', updatePointer, { passive: true });
  document.addEventListener('mousemove', updatePointer, { passive: true });

  activeSession = session;
  session.rafId = requestAnimationFrame(applyAutoPan);
}

export function stopDrawAutoPan() {
  if (!activeSession) return;

  const session = activeSession;
  activeSession = null;

  if (session.rafId) {
    cancelAnimationFrame(session.rafId);
    session.rafId = null;
  }

  if (session.moveKey) {
    unByKey(session.moveKey);
    session.moveKey = null;
  }

  if (session.dragKey) {
    unByKey(session.dragKey);
    session.dragKey = null;
  }

  document.removeEventListener('pointermove', session.leaveHandler);
  document.removeEventListener('mousemove', session.leaveHandler);
}

document.addEventListener('application-reset-active-tools', stopDrawAutoPan);
